// app_client/src/components/modals/UserModal.jsx
import React, { useState } from 'react';
import { X, Save, Eye, EyeOff, Wand2 } from 'lucide-react';

const generateSecurePassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()-_=+[]{};:,.?';
  const all = upper + lower + numbers + symbols;
  const length = 14;

  const requiredChars = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ];

  const rest = Array.from({ length: length - requiredChars.length }, () => (
    all[Math.floor(Math.random() * all.length)]
  ));

  return [...requiredChars, ...rest]
    .sort(() => Math.random() - 0.5)
    .join('');
};

const normalizeRoleName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .replace(/[^a-z0-9]/g, '');

const UserModal = ({ isOpen, onClose, onSave, user, roles }) => {
  const isEditing = !!user;
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role?._id || user?.role || '',
    status: user?.status || 'Active'
  });

  // Rastgele Şifre Üretme Fonksiyonu
  const generatePassword = () => {
    const generated = generateSecurePassword();
    setFormData({ ...formData, password: generated });
    setShowPassword(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 text-slate-700">
      <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 border border-slate-100">
        
        {/* Header */}
        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-xl font-black tracking-tight text-slate-800">
              {isEditing ? 'Profili Düzenle' : 'Yeni Personel'}
            </h2>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
              Sistem erişim yetkilerini belirleyin
            </p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-white hover:shadow-sm rounded-2xl transition-all text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSave(formData, user?._id); }} className="p-8 space-y-5">
          
          {/* Ad Soyad */}
          <div className="group">
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1 tracking-tighter">Tam Adı</label>
            <input 
              type="text" required
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/5 focus:bg-white focus:border-blue-500/20 outline-none transition-all font-bold text-sm"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>

          {/* E-Posta */}
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1 tracking-tighter">E-Posta Adresi</label>
            <input 
              type="email" required
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/5 focus:bg-white focus:border-blue-500/20 outline-none transition-all font-bold text-sm"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>

          {/* Şifre (Sadece Yeni Kayıtta) */}
          {!isEditing && (
            <div className="animate-in slide-in-from-top-2">
              <div className="flex justify-between items-end mb-2 ml-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">Başlangıç Şifresi</label>
                <button 
                  type="button" 
                  onClick={generatePassword}
                  className="text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase flex items-center gap-1 transition-colors"
                >
                  <Wand2 size={10} /> Şifre Üret
                </button>
              </div>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"} 
                  required
                  minLength={12}
                  className="w-full p-4 pr-12 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/5 focus:bg-white focus:border-blue-500/20 outline-none transition-all font-mono font-bold text-sm"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          )}

          {/* Rol Seçimi */}
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-2 ml-1 tracking-tighter">Sistem Rolü</label>
            <select 
              required
              className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-4 focus:ring-blue-500/5 focus:bg-white focus:border-blue-500/20 outline-none transition-all font-bold text-sm appearance-none cursor-pointer"
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
            >
              <option value="">Rol Seçin...</option>
              {(Array.isArray(roles) ? roles : [])
                .filter((role) => normalizeRoleName(role?.name) !== 'superadmin')
                .map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>

          {/* Aksiyon Butonları */}
          <div className="flex gap-4 pt-6">
            <button 
              type="button" 
              onClick={onClose} 
              className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 font-black uppercase text-[10px] tracking-[0.2em] rounded-2xl transition-all"
            >
              İptal
            </button>
            <button 
              type="submit" 
              className="flex-2 py-4 bg-slate-900 hover:bg-black text-white font-black uppercase text-[10px] tracking-[0.2em] rounded-2xl transition-all shadow-xl shadow-slate-900/20 flex items-center justify-center gap-2 active:scale-95"
            >
              <Save size={16} />
              Kullanıcıyı Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
